import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Authentication and role-based access control
const ProtectedRoute = ({ children, requiredRole = null, requiredRoles = [] }) => {
  const location = useLocation();
  const { user, loading, isInitialized } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Wait for AuthContext to initialize
    if (!isInitialized) {
      return;
    }

    // Check authentication and role permissions
    const checkAuthAndAccess = () => {
      try {
        let currentUser = null;
        let userRole = null;

        // Check localStorage first for role information, then AuthContext
        const currentUserStr = localStorage.getItem('currentUser');
        if (currentUserStr) {
          currentUser = JSON.parse(currentUserStr);
          userRole = currentUser.role || 'viewer';
        } else if (user) {
          // Fallback to AuthContext if no localStorage data
          currentUser = user;
          userRole = user.user_metadata?.role || 'viewer';
        }

        if (currentUser) {
          setIsAuthenticated(true);
          
          // Check role-based access
          if (requiredRole || requiredRoles.length > 0) {
            const allowedRoles = requiredRoles.length > 0 ? requiredRoles : [requiredRole];
            const hasAccess = allowedRoles.includes(userRole);
            setHasAccess(hasAccess);
          } else {
            // No role requirement, allow access
            setHasAccess(true);
          }
        } else {
          // If no user data found but we have a user from AuthContext, retry a few times
          if (user && retryCount < 3) {
            setRetryCount(prev => prev + 1);
            setTimeout(() => {
              checkAuthAndAccess();
            }, 200 * (retryCount + 1)); // Increasing delay
            return;
          }
          setIsAuthenticated(false);
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setIsAuthenticated(false);
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    // Add a small delay to ensure user data is fully loaded after login
    const timeoutId = setTimeout(() => {
      checkAuthAndAccess();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [user, isInitialized, requiredRole, requiredRoles, retryCount]);

  // Show loading while AuthContext is initializing or checking auth
  if (!isInitialized || loading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to unauthorized page if user doesn't have required role
  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 text-red-500">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;